import INotification from "@models/INotification";
import IPOShipmentOrderReservation, { IPOShipmentLinkedOrderReceipt } from "@models/IPOShipmentOrderReservation";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";

/**
 * Build the toast shown after a PO shipment is finalized when the PO is linked to a single sales order.
 *
 * - Something was reserved: success, listing each item and qty reserved for the order.
 * - The reservation failed: warning, so the user knows to allocate on the order themselves.
 * - Units were recorded on the PO only (non-inventory / drop-ship order): say so, so nobody looks for them in stock.
 * - Inventory was received for a shipped / cancelled order or a non-inventory order: say why it was not reserved.
 * - Otherwise (setting off, nothing pending, order not eligible): no toast.
 */
export const buildOrderReservationNotification = (reservation?: IPOShipmentOrderReservation): INotification | null => {
    if (!reservation) {
        return null;
    }

    const orderName = reservation.orderName || `#${reservation.orderId}`;

    if (reservation.skippedReason === "error") {
        return {
            title: "received_stock_not_reserved_for_order",
            message: orderName,
            level: NotificationLevelEnum.warning,
            position: NotificationPositionEnum.bc,
            skipMessageTranslate: true,
        };
    }

    // Non-inventory / drop-ship order: what it needed was recorded on the PO, not added to stock.
    const recordedItems = reservation.items.filter((item) => Number(item.qtyRecordedOnly || 0) > 0);

    if (recordedItems.length) {
        const recordedSummary = recordedItems
            .map((item) => `${item.qtyRecordedOnly} × ${item.sku || item.name || item.catalogItemId}`)
            .join(", ");

        return {
            title: "received_stock_recorded_only_for_order",
            message: `${orderName}: ${recordedSummary}`,
            level: NotificationLevelEnum.info,
            position: NotificationPositionEnum.bc,
            skipMessageTranslate: true,
        };
    }

    // Only explain a skip when inventory was actually received; non-inventory receipts have nothing to reserve.
    if (reservation.items.length && reservation.skippedReason === "order_closed") {
        return {
            title: "received_stock_not_reserved_order_closed",
            message: orderName,
            level: NotificationLevelEnum.info,
            position: NotificationPositionEnum.bc,
            skipMessageTranslate: true,
        };
    }

    if (reservation.items.length && reservation.skippedReason === "noninventory_order") {
        return {
            title: "received_stock_not_reserved_noninventory_order",
            message: orderName,
            level: NotificationLevelEnum.warning,
            position: NotificationPositionEnum.bc,
            skipMessageTranslate: true,
        };
    }

    const reservedItems = reservation.items.filter((item) => item.qtyReserved > 0);

    if (reservation.totalQtyReserved <= 0 || !reservedItems.length) {
        return null;
    }

    const itemSummary = reservedItems.map((item) => `${item.qtyReserved} × ${item.sku || item.name || item.catalogItemId}`).join(", ");

    return {
        title: "received_stock_reserved_for_order",
        message: `${orderName}: ${itemSummary}`,
        level: NotificationLevelEnum.success,
        position: NotificationPositionEnum.bc,
        skipMessageTranslate: true,
    };
};

/**
 * Translation key for the note under the shipment's Finalize button, saying what finalizing will do with stock for
 * the PO's linked sales order. Its message takes the order name as {order}. Null when there is nothing to say: no
 * linked order, or the auto-reserve setting is off and receiving works as it always has.
 */
export const getLinkedOrderReceiptNoteKey = (receipt?: IPOShipmentLinkedOrderReceipt): string | null => {
    if (!receipt) {
        return null;
    }

    if (receipt.mode === "reserve") {
        return "po_receive_note_reserve";
    }

    if (receipt.mode === "record_only") {
        return receipt.skippedReason === "dropship_order"
            ? "po_receive_note_record_only_dropship"
            : "po_receive_note_record_only_noninventory";
    }

    // mode "none": only worth a note when the order is why (not when the setting is simply off).
    return receipt.skippedReason ? "po_receive_note_not_reserved" : null;
};

/**
 * Translation key for a second line under the main note when the shipment has serial-tracked items whose stock comes
 * from their serials: those are neither reserved nor recorded only. Null when there is no main note to qualify.
 */
export const getLinkedOrderReceiptSerialNoteKey = (receipt?: IPOShipmentLinkedOrderReceipt): string | null => {
    if (!receipt?.serializedItemsExcluded || !getLinkedOrderReceiptNoteKey(receipt)) {
        return null;
    }

    return "po_receive_note_serialized_excluded";
};
